import React, { useState } from 'react';

interface ComboData {
  id: string;
  name: string;
  description: string;
  championName: string;
  difficulty: string;
  category: string;
  type: 'simple-combo' | 'jungle-clear';
  inputSequence: string;
  detailedSequence: any[];
}

interface ComboSelectorProps {
  combos: ComboData[];
  selectedCombo: ComboData | null;
  onComboSelect: (combo: ComboData | null) => void;
  customSequence: string;
  onCustomSequenceChange: (sequence: string) => void;
  loading: boolean;
}

const ComboSelector: React.FC<ComboSelectorProps> = ({
  combos,
  selectedCombo,
  onComboSelect,
  customSequence,
  onCustomSequenceChange,
  loading
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  if (loading) {
    return (
      <div className="loading-state">
        <p>Loading combo sequences...</p>
      </div>
    );
  }

  const handleComboSelect = (combo: ComboData) => {
    onComboSelect(combo);
    setIsDropdownOpen(false);
  };

  const handleCustomSequenceChange = (sequence: string) => {
    onCustomSequenceChange(sequence);
    if (sequence.trim()) {
      onComboSelect(null);
    }
  };

  return (
    <div className="combo-selection">
      <h3>Select a Combo</h3>
      
      {/* Dropdown for combo selection */}
      <div className="combo-dropdown-container">
        <div 
          className={`combo-dropdown-header ${isDropdownOpen ? 'open' : ''}`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <div className="selected-combo-display">
            {selectedCombo ? (
              <div className="cip-combo-header">
                <div className="cip-combo-name">{selectedCombo.name}</div>
                <div className="combo-meta">
                  <span className={`difficulty ${selectedCombo.difficulty}`}>
                    {selectedCombo.difficulty}
                  </span>
                  <span className="category">{selectedCombo.category}</span>
                </div>
              </div>
            ) : combos.length > 0 ? (
              <div className="cip-combo-header">
                <div className="cip-combo-name">{combos[0].name}</div>
                <div className="combo-meta">
                  <span className={`difficulty ${combos[0].difficulty}`}>
                    {combos[0].difficulty}
                  </span>
                  <span className="category">{combos[0].category}</span>
                </div>
              </div>
            ) : (
              <div className="placeholder-text">
                Select a combo...
              </div>
            )}
          </div>
          <div className={`dropdown-arrow ${isDropdownOpen ? 'up' : 'down'}`}>
            ▼
          </div>
        </div>
        
        {isDropdownOpen && (
          <div className="combo-dropdown-list">
            {combos.map((combo) => (
              <div
                key={combo.id}
                className={`combo-dropdown-item ${selectedCombo === combo ? 'selected' : ''}`}
                onClick={() => handleComboSelect(combo)}
              >
                <div className="cip-combo-header">
                  <div className="cip-combo-name">{combo.name}</div>
                  <div className="combo-meta">
                    <span className={`difficulty ${combo.difficulty}`}>
                      {combo.difficulty}
                    </span>
                    <span className="category">{combo.category}</span>
                  </div>
                </div>
                <div className="combo-description">{combo.description}</div>
                <div className="combo-champion">Champion: {combo.championName}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom sequence input */}
      <div className="custom-sequence">
        <h4>Or Enter Custom Sequence</h4>
        <input
          type="text"
          value={customSequence}
          onChange={(e) => handleCustomSequenceChange(e.target.value)}
          placeholder="Enter your custom combo sequence"
          className="custom-sequence-input"
        />
      </div>
    </div>
  );
};

export default ComboSelector;